import { useEffect, useState } from "react";
import { useAppSelector } from "hooks";
import { fetchWorkSecurityAreas, WorkSecurityArea } from "services/work-security-areas.service";
import { canSeeWsApprovalsFor } from "./work-security-approvals.slice";

/**
 * The spheres the current user manages (Master: all; otherwise the spheres
 * they are responsible for or hold the legacy permission of) — what
 * GET /work-security/areas answers. Individual pareigybės of a sphere may be
 * added/removed only by those people (positions catalogue §1 rule 13).
 * Nobody without Tvarkos access is asked at all.
 */
export function useManagedAreasState(companyId: string | undefined): {
  areas: WorkSecurityArea[];
  loaded: boolean;
} {
  const role = useAppSelector((state) => state.authReducer.role);
  const mayAsk = canSeeWsApprovalsFor(role, companyId);
  const [areas, setAreas] = useState<WorkSecurityArea[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setAreas([]);
    if (!companyId || !mayAsk) {
      // Nobody to ask: the answer "no spheres" is already final.
      setLoaded(true);
      return undefined;
    }
    setLoaded(false);
    let cancelled = false;
    fetchWorkSecurityAreas(companyId)
      .then((data) => {
        if (!cancelled) setAreas(data.areas || []);
      })
      .catch(() => {
        if (!cancelled) setAreas([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, mayAsk]);

  return { areas, loaded };
}

export function useManagedAreas(companyId: string | undefined): WorkSecurityArea[] {
  return useManagedAreasState(companyId).areas;
}
